import React from 'react';
import DatePicker from './../DatePicker';
import { CampoFiltro, SelectOperador } from '../styles';

interface CampoComOperadorProps {
  titulo: string;
  placeholder: string;
  operador: 'igual' | 'maior' | 'menor' | 'entre';
  valor?: string | number;
  valorMin?: string | number;
  valorMax?: string | number;
  tipo: 'text' | 'number' | 'date';
  onOperadorChange: (operador: 'igual' | 'maior' | 'menor' | 'entre') => void;
  onValorChange: (valor: string) => void;
  onValorMinChange?: (valor: string) => void;
  onValorMaxChange?: (valor: string) => void;
}

export default function CampoComOperador({
  titulo,
  placeholder,
  operador,
  valor,
  valorMin,
  valorMax,
  tipo,
  onOperadorChange,
  onValorChange,
  onValorMinChange,
  onValorMaxChange
}: CampoComOperadorProps) {
  const opcoesOperador = [
    { value: 'igual', label: 'Igual a' },
    { value: 'maior', label: 'Maior que' },
    { value: 'menor', label: 'Menor que' },
    { value: 'entre', label: 'Entre' }
  ];

  const formatCurrencyDisplay = (value?: string | number): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    }
    const numbersOnly = value.replace(/\D/g, '');
    if (!numbersOnly) return '';
    const numeric = parseInt(numbersOnly, 10) / 100;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numeric);
  };

  return (
    <CampoFiltro>
      <label style={{
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'var(--c2d3748)',
        marginBottom: '0.5rem',
        display: 'block'
      }}>
        {titulo}
      </label>
      
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', position: 'relative' }}>
        <SelectOperador
          value={operador}
          onChange={(e) => onOperadorChange(e.target.value as 'igual' | 'maior' | 'menor' | 'entre')}
        >
          {opcoesOperador.map(opcao => (
            <option key={opcao.value} value={opcao.value}>
              {opcao.label}
            </option>
          ))}
        </SelectOperador>

        {operador === 'entre' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
            {/* Primeiro campo - Valor mínimo */}
            <div>
              {tipo === 'date' ? (
                <div style={{ marginTop: '8px' }}>
                  <DatePicker
                    placeholder="Data Inicial"
                    value={valorMin?.toString() || ''}
                    onChange={(date) => onValorMinChange?.(date)}
                  />
                </div>
              ) : (
              <input
                placeholder="Valor mínimo"
                value={tipo === 'number' ? formatCurrencyDisplay(valorMin) : (valorMin?.toString() || '')}
                onChange={(e) => onValorMinChange?.(e.target.value)}
                type={tipo === 'number' ? 'text' : tipo}
                  style={{
                    width: '100%',
                    height: '48px',
                    background: 'var(--cffffff)',
                    border: '1px solid #e2e8f0',
                    outline: 'none',
                    borderRadius: '8px',
                    color: 'var(--c2d3748)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '24px',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    marginTop: '8px'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid #a0aec0';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #e2e8f0';
                  }}
                />
              )}
            </div>

            {/* Segundo campo - Valor máximo */}
            <div>
              {tipo === 'date' ? (
                <div>
                  <DatePicker
                    placeholder="Data Final"
                    value={valorMax?.toString() || ''}
                    onChange={(date) => onValorMaxChange?.(date)}
                  />
                </div>
              ) : (
              <input
                placeholder="Valor máximo"
                value={tipo === 'number' ? formatCurrencyDisplay(valorMax) : (valorMax?.toString() || '')}
                onChange={(e) => onValorMaxChange?.(e.target.value)}
                type={tipo === 'number' ? 'text' : tipo}
                  style={{
                    width: '100%',
                    height: '48px',
                    background: 'var(--cffffff)',
                    border: '1px solid #e2e8f0',
                    outline: 'none',
                    borderRadius: '8px',
                    color: 'var(--c2d3748)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '24px',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid #a0aec0';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #e2e8f0';
                  }}
                />
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1 }}>
            {tipo === 'date' ? (
              <div style={{ marginTop: '8px' }}>
                <DatePicker
                  placeholder={placeholder}
                  value={valor?.toString() || ''}
                  onChange={onValorChange}
                />
              </div>
            ) : (
              <input
                placeholder={tipo === 'number' ? 'R$ 0,00' : placeholder}
                value={tipo === 'number' ? formatCurrencyDisplay(valor) : (valor?.toString() || '')}
                onChange={(e) => onValorChange(e.target.value)}
                type={tipo === 'number' ? 'text' : tipo}
                style={{
                  width: '100%',
                  height: '48px',
                  background: 'var(--cffffff)',
                  border: '1px solid #e2e8f0',
                  outline: 'none',
                  borderRadius: '8px',
                  color: 'var(--c2d3748)',
                  padding: '12px 16px',
                  fontSize: '16px',
                  fontWeight: '400',
                  lineHeight: '24px',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  marginTop: '8px'
                }}
                onFocus={(e) => {
                  e.target.style.border = '1px solid #a0aec0';
                }}
                onBlur={(e) => {
                  e.target.style.border = '1px solid #e2e8f0';
                }}
              />
            )}
          </div>
        )}
      </div>
    </CampoFiltro>
  );
};
